import React from "react";
import { compact, find, map, reduce, sumBy } from "lodash";
import HorizontalStackedBarChart from "@charty/horizontal-stacked-bar-chart";
import { Gauge } from "@charty/horizontal-stacked-bar-chart/model";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizHorizontalStackedBarChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentPivot, getTermFromAggs } from "@radarly/models/search";
import Empty from "../../empty";

export interface SharedWidgetsPivotHorizontalStackedBarChartInnerProps {
  loading: boolean;
  data: SearchFragmentPivot["data"];
  fragment: SearchFragmentPivot;
  dataviz: DatavizHorizontalStackedBarChart;
  pivot: FullFacet;
  against: FullFacet;
  onClickGauge: (uid: Gauge["id"], range?: any, filter?: Gauge["id"]) => void;
  className?: string;
  isFullscreen?: boolean;
  isPNG?: boolean;
}

function getGauges(
  data: SearchFragmentPivot["data"]["aggs"]["any"]["values"],
  metric: Metric,
  pivot: FullFacet,
  against: FullFacet,
  locale: Locale
) {
  return compact(
    map(pivot.values, (serie) => {
      const pivotBucket = find(data, (entries) => {
        if (pivot.id === Facet.Focuses) {
          return entries.key === "focus_" + serie.id;
        } else {
          return entries.key === serie.id;
        }
      });
      if (pivotBucket) {
        return {
          id: serie.id,
          label:
            serie.label || getFacetName(locale, pivot.id as Facet, serie.id),
          series: map(against.values, (againstSerie) => {
            const againstBucket = find(
              pivotBucket.aggs[against.id]
                ? pivotBucket.aggs[against.id].values
                : [],
              (entries) => {
                if (against.id === Facet.Focuses) {
                  return entries.key === "focus_" + againstSerie.id;
                } else {
                  return entries.key === againstSerie.id;
                }
              }
            );
            return {
              id: againstSerie.id,
              label:
                againstSerie.label ||
                getFacetName(locale, against.id as Facet, againstSerie.id),
              count: againstBucket ? againstBucket.counts[metric] : 0,
              color: againstSerie.color,
            };
          }),
        };
      } else {
        return null;
      }
    })
  );
}

export default function SharedWidgetsPivotHorizontalStackedBarChartInner({
  loading,
  data,
  fragment,
  dataviz,
  pivot,
  against,
  onClickGauge,
  className,
  isFullscreen,
  isPNG,
}: SharedWidgetsPivotHorizontalStackedBarChartInnerProps) {
  const isChartValuesFormatEnabled = useFeature("chart_values_format");
  const locale = useCurrentUserLocale();
  const field = getTermFromAggs(fragment.request.aggs);
  const gauges = getGauges(
    data && data.aggs && data.aggs[field] ? data.aggs[field].values : [],
    fragment.request.aggs[field].metrics[0],
    pivot,
    against,
    locale
  );
  const filteredTotal = reduce(
    gauges,
    (acc, gauge) => {
      return acc + sumBy(gauge.series, "count");
    },
    0
  );

  return (
    <Empty empty={!data || !data.total || !filteredTotal}>
      <HorizontalStackedBarChart
        className={className}
        onClickGauge={onClickGauge}
        gauges={gauges}
        limit={5}
        locale={locale}
        sort={dataviz.sort}
        isFullscreen={isFullscreen}
        metric={fragment.request.aggs[field].metrics[0]}
        displayVolumes={dataviz.displayVolumes}
        isPNG={isPNG}
        chartValuesFormat={
          !isChartValuesFormatEnabled
            ? "relative"
            : dataviz.chartValuesFormat || "relative"
        }
      />
    </Empty>
  );
}
